import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import useLocalStorage from "react-use-localstorage";

import { useUser } from "@/context/user-context";
import moment from "moment";
import mcpNode from "../assets/new_feature_popup/mcp-node.png";
import modelPricing from "../assets/new_feature_popup/model-pricing.gif";

const featureVersion = "07-02-2025";
const STORAGE_KEY_VERSION = "lastSeenFeatureVersion";
const STORAGE_KEY_DISMISSED = "featureModalDismissed";

export default function NewFeatureModal() {
  const { user } = useUser();
  const { created_at } = user;

  const featureReleaseDate = moment(featureVersion, "MM-DD-YYYY");
  const userCreatedDate = moment(created_at);
  const isSignedUpAfterRelease = userCreatedDate.isAfter(featureReleaseDate);

  const [lastSeenFeatureVersion, setLastSeenFeatureVersion] = useLocalStorage(
    STORAGE_KEY_VERSION,
    "",
  );

  const [isDismissed, setIsDismissed] = useLocalStorage(
    `${STORAGE_KEY_DISMISSED}_${featureVersion}`,
    "false",
  );

  const [isOpen, setIsOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(0);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (isSignedUpAfterRelease && lastSeenFeatureVersion !== featureVersion) {
      setLastSeenFeatureVersion(featureVersion);
      setIsDismissed("true");
    }
  }, [
    isSignedUpAfterRelease,
    lastSeenFeatureVersion,
    setLastSeenFeatureVersion,
    setIsDismissed,
  ]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setLastSeenFeatureVersion(featureVersion);
    setIsDismissed("true");
  }, [setLastSeenFeatureVersion, setIsDismissed]);

  const modals: Array<{
    title: string;
    subtitle: string;
    image?: string;
    video?: string;
    link?: string;
    content?: string;
  }> = [
    {
      title: "Conversation Simulator Evals",
      subtitle:
        "Use the new eval type to automate back-and-forth conversations between your AI agent and simulated users.",
      video: "https://www.youtube.com/embed/VgsExVpD8_E",
      link: "https://docs.promptlayer.com/features/evaluations/eval-types#conversation-simulator",
    },
    {
      title: "MCP available in agents and evals",
      subtitle:
        "Easily connect and run functions on your MCP servers directly from your PromptLayer agents.",
      image: mcpNode,
      link: "https://docs.promptlayer.com/features/evaluations/eval-types#mcp",
    },
    {
      title: "Model cost comparison tool",
      subtitle:
        "Can't keep up with all the new models? Neither can we. Compare costs across models directly from the playground. ",
      image: modelPricing,
    },
  ];

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const shouldShowModal =
      !isSignedUpAfterRelease &&
      lastSeenFeatureVersion !== featureVersion &&
      isDismissed !== "true";

    if (shouldShowModal) setIsOpen(true);
  }, [lastSeenFeatureVersion, isSignedUpAfterRelease, isDismissed]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        closeModal();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, closeModal]);

  const nextModal = () => {
    setActiveModal((prev) => (prev + 1) % modals.length);
  };

  const prevModal = () => {
    setActiveModal((prev) => (prev === 0 ? modals.length - 1 : prev - 1));
  };

  const renderCarouselControls = () => {
    if (modals.length <= 1) return null;

    return (
      <div className="flex items-center justify-between">
        <button
          className="text-2xl text-blue-500 hover:text-blue-600"
          onClick={prevModal}
        >
          &#8592;
        </button>
        <div className="flex space-x-2">
          {modals.map((_, index) => (
            <button
              key={index}
              className={`h-3 w-3 rounded-full focus:outline-none ${
                index === activeModal ? "bg-blue-500" : "bg-gray-300"
              }`}
              onClick={() => setActiveModal(index)}
            ></button>
          ))}
        </div>
        <button
          className="text-2xl text-blue-500 hover:text-blue-600"
          onClick={nextModal}
        >
          &#8594;
        </button>
      </div>
    );
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-800/30 backdrop-blur-sm"></div>
          <div className="relative z-10 mx-4 bg-white p-6 shadow-lg md:mx-auto md:w-full md:max-w-md md:rounded-lg lg:max-w-2xl">
            <button
              onClick={closeModal}
              className="absolute right-4 top-4 text-2xl font-bold text-gray-400 hover:text-gray-600"
              aria-label="Close modal"
            >
              <X className="size-5" />
            </button>
            <div className="max-h-[90vh] overflow-y-auto">
              <h2 className="mb-4 border-b pb-2 text-2xl font-semibold">
                New features! 🍰
              </h2>
              <h2 className="mb-2 text-xl font-medium">
                {modals[activeModal].title}
                {modals[activeModal].link && (
                  <a
                    target="_blank"
                    href={modals[activeModal].link}
                    className="cursor-pointer pl-2 text-xs text-blue-500 hover:text-blue-400"
                    rel="noreferrer"
                  >
                    (Read the docs)
                  </a>
                )}
              </h2>
              <h3 className="text-md">{modals[activeModal].subtitle}</h3>
              {modals[activeModal].video && (
                <iframe
                  className="mx-auto mt-4 h-64 w-full rounded-lg"
                  src={modals[activeModal].video}
                  title={modals[activeModal].title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              )}
              {modals[activeModal].image && (
                <img
                  className="mx-auto mt-4 h-auto max-h-96 w-full object-contain"
                  src={modals[activeModal].image}
                  alt={modals[activeModal].title}
                />
              )}
              <p className="mb-6">{modals[activeModal].content}</p>
              {renderCarouselControls()}
              <div className="mt-6 flex justify-end">
                <Button onClick={closeModal}>Got it!</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
